import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'

interface CopyDialogProps {
  open: boolean
  title: string
  initialName: string
  existingNames: string[]
  onClose: () => void
  onCopy: (newName: string, newInternalId: string) => void
  loading?: boolean
  initialInternalId?: string
  existingInternalIds?: string[]
}

export const CopyDialog = ({
  open,
  title,
  initialName,
  existingNames,
  onClose,
  onCopy,
  loading = false,
  initialInternalId,
  existingInternalIds = [],
}: CopyDialogProps) => {
  const [newName, setNewName] = useState('')
  const [newInternalId, setNewInternalId] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [internalIdError, setInternalIdError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setNewName(initialName + '_copy')
      setNewInternalId(initialInternalId ? initialInternalId + '_copy' : '')
      setNameError(null)
      setInternalIdError(null)
    }
  }, [open, initialName, initialInternalId])

  const handleCopy = () => {
    let valid = true

    if (!newName.trim()) {
      setNameError('Name is required')
      valid = false
    } else if (existingNames.includes(newName.trim())) {
      setNameError('Name already exists')
      valid = false
    }

    if (initialInternalId !== undefined) {
      if (!newInternalId.trim()) {
        setInternalIdError('Internal ID is required')
        valid = false
      } else if (existingInternalIds.includes(newInternalId.trim())) {
        setInternalIdError('Internal ID already exists')
        valid = false
      }
    }

    if (!valid) return

    onCopy(newName.trim(), newInternalId.trim())
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {initialInternalId !== undefined && (
            <TextField
              fullWidth
              label='Internal ID'
              value={newInternalId}
              onChange={e => {
                setNewInternalId(e.target.value)
                setInternalIdError(null)
              }}
              error={!!internalIdError}
              helperText={internalIdError}
              disabled={loading}
            />
          )}
          <TextField
            fullWidth
            label='New Name'
            value={newName}
            onChange={e => {
              setNewName(e.target.value)
              setNameError(null)
            }}
            error={!!nameError}
            helperText={nameError}
            disabled={loading}
            autoFocus
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleCopy} variant='contained' disabled={loading}>
          Copy
        </Button>
      </DialogActions>
    </Dialog>
  )
}
